import { useState, useEffect } from "react";
import { Outlet, NavLink, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "../contexts/AuthContext";
import TenantSwitcher from "./TenantSwitcher";
import axios from "axios";
import {
  Shield,
  LayoutDashboard,
  FileWarning,
  Grid3X3,
  FileSearch,
  Users,
  Settings,
  Bell,
  Menu,
  ChevronRight,
  ChevronDown,
  Building2,
  Network,
  Brain,
  DollarSign,
  Building,
  Target,
  FileText,
  ClipboardList,
  Gauge,
  Globe,
  ShieldCheck,
  ArrowUpCircle,
  FileCheck,
  GitBranch,
  TrendingUp,
  Dice5,
  Radar,
  Compass,
  Briefcase,
  Scale,
  Lightbulb,
  ScrollText,
  ClipboardCheck,
  UserCheck,
  History,
  Zap,
  LogOut,
  User,
  Layers,
  Sun,
  Moon,
  BookOpen,
} from "lucide-react";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { ScrollArea } from "./ui/scroll-area";

// Grouped navigation structure — each item has an optional `feature` key for subscription gating
const navGroups = [
  {
    id: "top",
    label: null, // No group header - standalone items
    icon: null,
    items: [
      { path: "/app", icon: LayoutDashboard, label: "Dashboard", feature: "dashboard" },
      { path: "/app/setup", icon: Building2, label: "Business Setup", feature: "business_setup" },
    ],
  },
  {
    id: "erm",
    label: "ERM",
    icon: Shield,
    items: [
      { path: "/app/risks", icon: FileWarning, label: "Risk Register", feature: "risk_register" },
      { path: "/app/matrix", icon: Grid3X3, label: "Risk Matrix", feature: "risk_matrix" },
      { path: "/app/strategic", icon: Target, label: "Strategic Risk", feature: "strategic_risk" },
      { path: "/app/emerging-risks", icon: Compass, label: "Emerging Risks", feature: "emerging_risks" },
      { path: "/app/intelligence", icon: Radar, label: "External Intelligence", feature: "external_intelligence" },
      { path: "/app/documents", icon: FileSearch, label: "Documents", feature: "documents" },
      { path: "/app/interviews", icon: Users, label: "Interviews", feature: "probing_interviews" },
      { path: "/app/culture", icon: Users, label: "Culture Assessment", feature: "culture_assessment" },
      { path: "/app/risk-surveys", icon: ClipboardCheck, label: "Risk Surveys", feature: "risk_surveys" },
    ],
  },
  {
    id: "financial",
    label: "Financial Risk",
    icon: DollarSign,
    items: [
      { path: "/app/financing", icon: DollarSign, label: "Risk Financing", feature: "risk_financing" },
      { path: "/app/captive", icon: Building, label: "Captive Management", feature: "captive_management" },
      { path: "/app/property", icon: Building2, label: "Property Management", feature: "property_management" },
      { path: "/app/coi", icon: FileCheck, label: "COI Tracking", feature: "coi_tracking" },
      { path: "/app/transfer", icon: Shield, label: "Risk Transfer", feature: "risk_transfer" },
      { path: "/app/correlation", icon: GitBranch, label: "Risk Correlation", feature: "risk_correlation" },
      { path: "/app/exposure", icon: TrendingUp, label: "Aggregate Exposure", feature: "aggregate_exposure" },
      { path: "/app/montecarlo", icon: Dice5, label: "Monte Carlo", feature: "monte_carlo" },
    ],
  },
  {
    id: "claims",
    label: "Claims Management",
    icon: ClipboardList,
    items: [
      { path: "/app/claims", icon: ClipboardList, label: "Claims Monitor", feature: "claims_monitoring" },
      { path: "/app/loss-events", icon: FileWarning, label: "Loss Events", feature: "loss_events" },
    ],
  },
  {
    id: "strategy",
    label: "Decisions & Strategy",
    icon: Lightbulb,
    items: [
      { path: "/app/coach", icon: Brain, label: "Decision Coach", feature: "decision_coach" },
      { path: "/app/decisions", icon: FileCheck, label: "Decision Dossiers", feature: "decision_dossier" },
      { path: "/app/scenarios", icon: Network, label: "Scenarios & Preparedness", feature: "scenario_planning" },
    ],
  },
  {
    id: "governance",
    label: "Risk Governance",
    icon: Scale,
    items: [
      { path: "/app/reports", icon: FileText, label: "Board Reports", feature: "board_reports" },
      { path: "/app/risk-appetite", icon: Target, label: "Risk Appetite", feature: "risk_appetite" },
      { path: "/app/tprm", icon: UserCheck, label: "Third Party Risk", feature: "tprm" },
      { path: "/app/actions", icon: ClipboardList, label: "Action Tracking", feature: "action_tracking" },
      { path: "/app/kris", icon: Gauge, label: "KRI Dashboard", feature: "kri_dashboard" },
      { path: "/app/controls", icon: ShieldCheck, label: "Control Library", feature: "controls" },
      { path: "/app/escalations", icon: ArrowUpCircle, label: "Escalations", feature: "escalations" },
      { path: "/app/alerts", icon: Bell, label: "Alert Center", feature: "alerts" },
      { path: "/app/approvals", icon: FileCheck, label: "Approvals", feature: "controls" },
      { path: "/app/history", icon: History, label: "History & Versions", feature: "controls" },
      { path: "/app/score-engine", icon: Zap, label: "AI Score Engine", feature: "risk_register" },
    ],
  },
];

// Flat list for page title lookup
const allNavItems = navGroups.flatMap((group) => group.items);

export const Layout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [theme, setTheme] = useState(() => localStorage.getItem("fr-theme") || "dark");

  useEffect(() => {
    document.documentElement.classList.toggle("light", theme === "light");
    localStorage.setItem("fr-theme", theme);
  }, [theme]);

  const toggleTheme = () => setTheme((t) => (t === "light" ? "dark" : "light"));
  const [expandedGroups, setExpandedGroups] = useState(["erm", "financial", "claims", "strategy", "governance"]);
  // TD-29 — Group View nav item is visible only when the user's tenant is a parent
  // (sig | group_captive | holding_company) with at least one child tenant.
  const [hasGroupAccess, setHasGroupAccess] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  useEffect(() => {
    if (!user) { setHasGroupAccess(false); return; }
    // Platform admins without a tenant context can't query group summary
    // (endpoint requires ?parent_tenant_id for them) — skip the probe.
    if (user.platform_role === "platform_admin" && !user.tenant_id) {
      setHasGroupAccess(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem("access_token");
        const params = user.platform_role === "platform_admin" && user.tenant_id
          ? `?parent_tenant_id=${user.tenant_id}` : "";
        const url = `${process.env.REACT_APP_BACKEND_URL}/api/group/summary${params}`;
        const { data } = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });
        if (!cancelled && data?.member_count > 0) setHasGroupAccess(true);
      } catch {
        if (!cancelled) setHasGroupAccess(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  // Feature gating — filter nav items based on subscription
  const allowedFeatures = user?.subscription?.features || [];
  const tierLabel = user?.subscription?.label || "";

  const filteredNavGroups = navGroups.map(group => ({
    ...group,
    items: group.items.filter(item => !item.feature || allowedFeatures.includes(item.feature)),
  })).filter(group => group.items.length > 0);

  const allFilteredItems = filteredNavGroups.flatMap(g => g.items);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const currentPage = allFilteredItems.find((item) => item.path === location.pathname);

  const toggleGroup = (groupId) => {
    setExpandedGroups((prev) =>
      prev.includes(groupId)
        ? prev.filter((id) => id !== groupId)
        : [...prev, groupId]
    );
  };

  // Check if any item in a group is active
  const isGroupActive = (group) => {
    return group.items.some((item) => item.path === location.pathname);
  };

  return (
    <div className="flex min-h-screen">
      {/* Mobile overlay */}
      <AnimatePresence>
        {sidebarOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 z-30 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}
      </AnimatePresence>

      {/* Sidebar */}
      <aside
        className={`sidebar ${sidebarOpen ? "open" : ""} lg:translate-x-0`}
        data-testid="sidebar"
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-white/5">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/20 glow-primary">
                <Shield className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h1 className="font-bold text-lg tracking-tight">FractionalRisk</h1>
                <p className="text-xs text-muted-foreground">ERM AI Agent</p>
                {tierLabel && <Badge variant="outline" className="text-[10px] px-1.5 py-0 mt-0.5 border-primary/30 text-primary/70">{tierLabel}</Badge>}
              </div>
            </div>
          </div>

          {/* Navigation */}
          <ScrollArea className="flex-1 py-4">
            <nav className="px-3 space-y-2">
              {filteredNavGroups.map((group) => {
                const GroupIcon = group.icon;
                const isExpanded = expandedGroups.includes(group.id);
                const hasActiveItem = isGroupActive(group);

                // Top-level items without group header
                if (!group.label) {
                  return (
                    <div key={group.id} className="space-y-0.5 mb-2">
                      {group.items.map((item) => {
                        const Icon = item.icon;
                        const isActive = location.pathname === item.path;

                        return (
                          <NavLink
                            key={item.path}
                            to={item.path}
                            onClick={() => setSidebarOpen(false)}
                            data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                          >
                            <motion.div
                              className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                                isActive
                                  ? "bg-primary/20 text-primary"
                                  : "text-muted-foreground hover:text-foreground hover:bg-white/5"
                              }`}
                              whileHover={{ x: 4 }}
                              transition={{ type: "spring", stiffness: 300, damping: 20 }}
                            >
                              <Icon className="w-4 h-4" />
                              <span className="text-sm font-medium">{item.label}</span>
                              {isActive && (
                                <ChevronRight className="w-3 h-3 ml-auto" />
                              )}
                            </motion.div>
                          </NavLink>
                        );
                      })}
                    </div>
                  );
                }

                return (
                  <div key={group.id} className="space-y-1">
                    {/* Group Header */}
                    <button
                      onClick={() => toggleGroup(group.id)}
                      className={`w-full flex items-center justify-between px-3 py-2 rounded-lg transition-colors ${
                        hasActiveItem
                          ? "bg-primary/10 text-primary"
                          : "text-muted-foreground hover:text-foreground hover:bg-white/5"
                      }`}
                      data-testid={`nav-group-${group.id}`}
                    >
                      <div className="flex items-center gap-3">
                        <GroupIcon className="w-4 h-4" />
                        <span className="text-sm font-semibold">{group.label}</span>
                      </div>
                      <motion.div
                        animate={{ rotate: isExpanded ? 0 : -90 }}
                        transition={{ duration: 0.2 }}
                      >
                        <ChevronDown className="w-4 h-4" />
                      </motion.div>
                    </button>

                    {/* Group Items */}
                    <AnimatePresence initial={false}>
                      {isExpanded && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.2 }}
                          className="overflow-hidden"
                        >
                          <div className="pl-4 space-y-0.5">
                            {group.items.map((item) => {
                              const Icon = item.icon;
                              const isActive = location.pathname === item.path;

                              return (
                                <NavLink
                                  key={item.path}
                                  to={item.path}
                                  onClick={() => setSidebarOpen(false)}
                                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                                >
                                  <motion.div
                                    className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                                      isActive
                                        ? "bg-primary/20 text-primary"
                                        : "text-muted-foreground hover:text-foreground hover:bg-white/5"
                                    }`}
                                    whileHover={{ x: 4 }}
                                    transition={{ type: "spring", stiffness: 300, damping: 20 }}
                                  >
                                    <Icon className="w-4 h-4" />
                                    <span className="text-sm">{item.label}</span>
                                    {isActive && (
                                      <ChevronRight className="w-3 h-3 ml-auto" />
                                    )}
                                  </motion.div>
                                </NavLink>
                              );
                            })}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              })}

              {/* Platform Admin - Tenant Management */}
              {user?.platform_role === "platform_admin" || user?.role === "admin" ? (
                <div className="pt-2 border-t border-white/5 mt-2 space-y-0.5">
                  <NavLink
                    to="/app/admin"
                    onClick={() => setSidebarOpen(false)}
                    data-testid="nav-platform-admin"
                  >
                    <motion.div
                      className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                        location.pathname === "/app/admin"
                          ? "bg-primary/20 text-primary"
                          : "text-muted-foreground hover:text-foreground hover:bg-white/5"
                      }`}
                      whileHover={{ x: 4 }}
                      transition={{ type: "spring", stiffness: 300, damping: 20 }}
                    >
                      <Shield className="w-4 h-4" />
                      <span className="text-sm">Platform Admin</span>
                    </motion.div>
                  </NavLink>
                  <NavLink
                    to="/app/tenants"
                    onClick={() => setSidebarOpen(false)}
                    data-testid="nav-tenants"
                  >
                    <motion.div
                      className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                        location.pathname === "/app/tenants"
                          ? "bg-primary/20 text-primary"
                          : "text-muted-foreground hover:text-foreground hover:bg-white/5"
                      }`}
                      whileHover={{ x: 4 }}
                      transition={{ type: "spring", stiffness: 300, damping: 20 }}
                    >
                      <Globe className="w-4 h-4" />
                      <span className="text-sm">Tenants</span>
                    </motion.div>
                  </NavLink>
                  <NavLink
                    to="/app/setup-guide"
                    onClick={() => setSidebarOpen(false)}
                    data-testid="nav-setup-guide"
                  >
                    <motion.div
                      className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                        location.pathname === "/app/setup-guide"
                          ? "bg-primary/20 text-primary"
                          : "text-muted-foreground hover:text-foreground hover:bg-white/5"
                      }`}
                      whileHover={{ x: 4 }}
                      transition={{ type: "spring", stiffness: 300, damping: 20 }}
                    >
                      <BookOpen className="w-4 h-4" />
                      <span className="text-sm">Client Setup Guide</span>
                    </motion.div>
                  </NavLink>
                </div>
              ) : null}

              {/* Org Admin - visible to group_admin and admin roles with tenant context */}
              {(user?.platform_role === "group_admin" || user?.platform_role === "platform_admin" || user?.role === "admin") ? (
                <div className={`${user?.platform_role !== "platform_admin" && user?.role !== "admin" ? "pt-2 border-t border-white/5 mt-2" : ""} space-y-0.5`}>
                  <NavLink
                    to="/app/org-admin"
                    onClick={() => setSidebarOpen(false)}
                    data-testid="nav-org-admin"
                  >
                    <motion.div
                      className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                        location.pathname === "/app/org-admin"
                          ? "bg-primary/20 text-primary"
                          : "text-muted-foreground hover:text-foreground hover:bg-white/5"
                      }`}
                      whileHover={{ x: 4 }}
                      transition={{ type: "spring", stiffness: 300, damping: 20 }}
                    >
                      <Building2 className="w-4 h-4" />
                      <span className="text-sm">Org Admin</span>
                    </motion.div>
                  </NavLink>
                </div>
              ) : null}

              {/* TD-29 — Group View: only when current tenant is a parent with children */}
              {hasGroupAccess && (
                <div className="pt-2 border-t border-white/5 mt-2">
                  <NavLink to="/app/group" onClick={() => setSidebarOpen(false)} data-testid="nav-group-view">
                    <motion.div
                      className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                        location.pathname === "/app/group"
                          ? "bg-primary/20 text-primary"
                          : "text-muted-foreground hover:text-foreground hover:bg-white/5"
                      }`}
                      whileHover={{ x: 4 }}
                      transition={{ type: "spring", stiffness: 300, damping: 20 }}
                    >
                      <Layers className="w-4 h-4" />
                      <span className="text-sm">Group View</span>
                    </motion.div>
                  </NavLink>
                </div>
              )}

              {/* Settings - standalone */}
              <div className="pt-2 border-t border-white/5 mt-2">
                <NavLink
                  to="/app/settings"
                  onClick={() => setSidebarOpen(false)}
                  data-testid="nav-settings"
                >
                  <motion.div
                    className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                      location.pathname === "/app/settings"
                        ? "bg-primary/20 text-primary"
                        : "text-muted-foreground hover:text-foreground hover:bg-white/5"
                    }`}
                    whileHover={{ x: 4 }}
                    transition={{ type: "spring", stiffness: 300, damping: 20 }}
                  >
                    <Settings className="w-4 h-4" />
                    <span className="text-sm">Settings</span>
                  </motion.div>
                </NavLink>
              </div>
            </nav>
          </ScrollArea>

          {/* User Profile */}
          <div className="p-4 border-t border-white/5">
            <div className="glass-card rounded-lg p-3">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center shrink-0">
                  <User className="w-4 h-4 text-primary" />
                </div>
                <div className="min-w-0">
                  <p data-testid="sidebar-user-name" className="text-sm font-medium text-foreground truncate">{user?.name || "User"}</p>
                  <p className="text-xs text-muted-foreground truncate capitalize">{user?.role?.replace("_", " ") || "Analyst"}</p>
                </div>
              </div>
              <button
                data-testid="logout-button"
                onClick={handleLogout}
                className="flex items-center gap-2 w-full px-2 py-1.5 rounded-md text-xs text-muted-foreground hover:text-red-400 hover:bg-red-500/10 transition-colors"
              >
                <LogOut className="w-3.5 h-3.5" />
                Sign Out
              </button>
            </div>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <main className="main-content flex-1 flex flex-col min-w-0 overflow-x-hidden">
        {/* Top bar */}
        <header className="topbar flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden"
              onClick={() => setSidebarOpen(true)}
              data-testid="mobile-menu-btn"
            >
              <Menu className="w-5 h-5" />
            </Button>
            <div>
              <h2 className="text-xl font-semibold tracking-tight">
                {currentPage?.label || "Dashboard"}
              </h2>
              <p className="text-sm text-muted-foreground">
                Enterprise Risk Management
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              data-testid="theme-toggle-btn"
              title={theme === "light" ? "Switch to dark mode" : "Switch to light mode"}
            >
              {theme === "light" ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="relative"
              data-testid="notifications-btn"
            >
              <Bell className="w-5 h-5" />
              <Badge className="absolute -top-1 -right-1 h-5 w-5 p-0 flex items-center justify-center bg-red-500 text-white text-xs">
                3
              </Badge>
            </Button>
            <div data-testid="topbar-user-info" className="hidden sm:flex items-center gap-2 text-sm text-muted-foreground">
              <TenantSwitcher />
              <span className="capitalize">{user?.name || ""}</span>
              <span className="text-xs px-2 py-0.5 rounded-full bg-primary/20 text-primary font-medium capitalize">{user?.role?.replace("_", " ") || ""}</span>
            </div>
          </div>
        </header>

        {/* Page content */}
        <div className="flex-1 p-6 pb-16">
          <AnimatePresence mode="wait">
            <motion.div
              key={location.pathname}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
            >
              <Outlet />
            </motion.div>
          </AnimatePresence>
        </div>

        {/* AI Disclaimer Footer */}
        <footer className="fixed bottom-0 right-0 left-0 md:left-64 bg-slate-900/95 backdrop-blur-sm border-t border-slate-700/50 py-2 px-4 z-40">
          <div className="flex items-center justify-center gap-2 text-xs text-slate-400">
            <span className="flex items-center gap-1">
              <span className="text-yellow-500">⚠️</span>
              <span className="font-medium">AI-Assisted Analysis</span>
            </span>
            <span className="text-slate-600">•</span>
            <span>Not legal, financial, or professional advice</span>
            <span className="text-slate-600">•</span>
            <span>Review with qualified advisors before acting</span>
            <span className="text-slate-600">•</span>
            <a href="/terms" className="text-primary hover:underline">Terms of Service</a>
          </div>
        </footer>
      </main>
    </div>
  );
};

export default Layout;
